/**
 * Article cover art, generated from the article slug.
 *
 * Every photograph the old site used is gone (HTTP 404 on the whole uploads
 * tree), so each article gets a deterministic geometric cover instead: the same
 * slug always produces the same composition, which keeps the Insights index
 * looking arranged rather than random, and keeps the page weight at zero
 * additional requests.
 *
 * `post.legacyImage` still records the photo that used to sit here. To go back
 * to photography, drop the files into public/images/posts/ and render an <img>
 * when `legacyImage` resolves.
 */

const GOLD = '#ccad00'
const LINE = 'rgba(255, 255, 255, 0.2)'
const LINE_SOFT = 'rgba(255, 255, 255, 0.1)'

/** Small deterministic string hash (FNV-1a, 32-bit). */
function hash(text) {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/* -------------------------------------------------------------------------- */
/* Compositions - each fills a 400x250 box                                    */
/* -------------------------------------------------------------------------- */

/** Descending chevrons, echoing the brand mark. */
function Chevrons({ seed }) {
  const rows = 5 + (seed % 3)
  const pitch = 250 / (rows + 1)
  return (
    <g>
      {Array.from({ length: rows }, (_, i) => {
        const y = pitch * (i + 0.4)
        const accent = i === seed % rows
        return (
          <path
            key={i}
            d={`M110 ${y} L200 ${y + 62} L290 ${y}`}
            stroke={accent ? GOLD : LINE}
            strokeWidth={accent ? 3 : 1.4}
            fill="none"
          />
        )
      })}
    </g>
  )
}

/** Concentric arcs, like a plan detail. */
function Arcs({ seed }) {
  const cx = 90 + (seed % 3) * 110
  return (
    <g fill="none">
      {Array.from({ length: 7 }, (_, i) => {
        const r = 40 + i * 30
        const accent = i === 2 + (seed % 3)
        return (
          <circle
            key={i}
            cx={cx}
            cy={250}
            r={r}
            stroke={accent ? GOLD : LINE}
            strokeWidth={accent ? 2.4 : 1.2}
          />
        )
      })}
      <path d="M0 250h400" stroke={LINE} strokeWidth="1.4" />
    </g>
  )
}

/** A skyline of blocks. */
function Skyline({ seed }) {
  const blocks = [
    { x: 40, w: 62 },
    { x: 112, w: 48 },
    { x: 170, w: 74 },
    { x: 254, w: 54 },
    { x: 318, w: 44 },
  ]
  return (
    <g fill="none">
      {blocks.map((block, i) => {
        const h = 60 + ((seed >> (i * 2)) % 5) * 26
        const accent = i === seed % blocks.length
        return (
          <g key={block.x}>
            <rect
              x={block.x}
              y={210 - h}
              width={block.w}
              height={h}
              stroke={accent ? GOLD : LINE}
              strokeWidth={accent ? 2 : 1.3}
              fill={accent ? GOLD : '#ffffff'}
              fillOpacity={accent ? 0.1 : 0.03}
            />
            <path
              d={`M${block.x + 8} ${210 - h + 16}h${block.w - 16}`}
              stroke={LINE_SOFT}
              strokeWidth="1"
            />
          </g>
        )
      })}
      <path d="M20 210h360" stroke={LINE} strokeWidth="1.4" />
    </g>
  )
}

/** A measured grid with one highlighted cell. */
function Grid({ seed }) {
  const cols = 8
  const rows = 5
  const pick = seed % (cols * rows)
  const cells = []
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const index = r * cols + c
      const accent = index === pick
      cells.push(
        <rect
          key={index}
          x={20 + c * 45}
          y={20 + r * 42}
          width="45"
          height="42"
          fill={accent ? GOLD : 'none'}
          fillOpacity={accent ? 0.9 : 0}
          stroke={accent ? GOLD : LINE_SOFT}
          strokeWidth={accent ? 2 : 1}
        />,
      )
    }
  }
  return <g>{cells}</g>
}

const COMPOSITIONS = [Chevrons, Arcs, Skyline, Grid]

/**
 * @param {object} props
 * @param {string} props.seed     Usually the post slug.
 * @param {string} [props.label]  Small caption, e.g. the category.
 * @param {string} [props.className]
 */
export default function Cover({ seed, label, className = '' }) {
  const h = hash(seed)
  const Composition = COMPOSITIONS[h % COMPOSITIONS.length]

  return (
    <div className={`cover ${className}`}>
      <svg
        viewBox="0 0 400 250"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
        focusable="false"
        fill="none"
      >
        <Composition seed={h} />
      </svg>
      {label ? <span className="cover__label">{label}</span> : null}
    </div>
  )
}
